import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Attachment,
  AttachmentInputProps
} from '@healthnz-prm/fhir-engine-renderer/types'
import { pdfjs } from 'react-pdf'
import { HugeiconsIcon } from '@hugeicons/react'
import { Upload01Icon } from '@hugeicons/core-free-icons'
import { InputFieldset } from '../inputFieldset'
import { Button } from '@/components/ui/button'
import { useUploadAttachment } from '@/hooks/useUploadAttachment'
import { useAuth } from '@/contexts/auth/auth'
import { FilePreview } from './FilePreview'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

export interface InputAttachmentProps {
  onChange?: (value: File) => void
  value?: File
}

import { SUPPORTED_ATTACHMENT_MIME_TYPES } from '@/lib/constants'

export function AttachmentInput(props: AttachmentInputProps) {
  const { disabled, error, inputId, onChange, value } = props

  const authToken = useAuth()
  const { questionnaireId } = useParams()
  const [file, setFile] = useState<File | null>(null)
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  )

  const {
    uploadAttachment,
    attachmentReference,
    loading: uploadLoading,
    error: uploadError
  } = useUploadAttachment({
    questionnaireId,
    authToken
  })

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0]
    if (!file || !SUPPORTED_ATTACHMENT_MIME_TYPES.includes(file.type)) {
      setValidationError('Unsupported file type')
      return
    }
    setValidationError(undefined)
    uploadAttachment(file)
    setFile(file)
  }

  const openFileDialog = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  useEffect(() => {
    if (attachmentReference)
      onChange?.({
        title: file?.name || '',
        contentType: file?.type || '',
        language: 'en-NZ',
        url: attachmentReference
      } as Attachment)
  }, [attachmentReference, file?.name, file?.type, onChange])

  // The input is unmounted when the page changes, so a file picked earlier is
  // gone while the attachment it produced is still in form state.
  const selectedFileName = file?.name || value?.title
  const hasAttachment = Boolean(file || value)

  return (
    <InputFieldset {...props} error={validationError || uploadError || error}>
      <div className='flex flex-col gap-2'>
        <p className='text-muted-foreground text-xs'>
          Note: only image and PDF files are supported
        </p>
        <FilePreview attachment={value} file={file} isLoading={uploadLoading} />
        <input
          disabled={disabled || uploadLoading}
          id={inputId}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={{ display: 'none' }}
          type='file'
        />
        <Button
          aria-describedby={
            selectedFileName
              ? `Replace selected file: ${selectedFileName}`
              : 'Upload file'
          }
          variant='outline'
          onClick={openFileDialog}
          disabled={disabled || uploadLoading}
        >
          <HugeiconsIcon icon={Upload01Icon} />
          {hasAttachment ? 'Replace file' : 'Upload file'}
        </Button>
      </div>
    </InputFieldset>
  )
}
